/**
 * Language Support Configuration
 *
 * This project supports 85 languages everywhere (English + 84 target languages).
 * Dubbing is limited to 29 languages due to ElevenLabs API limitations.
 */

export interface LanguageMetadata {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  isSource?: boolean;
}

/**
 * Canonical list of supported product languages (85 languages total: English + 84 target languages)
 */
export const LANGUAGE_METADATA: readonly LanguageMetadata[] = [
  {
    code: "en",
    name: "English",
    nativeName: "English",
    flag: "🇺🇸",
    isSource: true,
  },
  { code: "af", name: "Afrikaans", nativeName: "Afrikaans", flag: "🇿🇦" },
  { code: "am", name: "Amharic", nativeName: "አማርኛ", flag: "🇪🇹" },
  { code: "ar", name: "Arabic", nativeName: "العربية", flag: "🇸🇦" },
  {
    code: "az",
    name: "Azerbaijani",
    nativeName: "Azərbaycan dili",
    flag: "🇦🇿",
  },
  { code: "be", name: "Belarusian", nativeName: "Беларуская", flag: "🇧🇾" },
  { code: "bg", name: "Bulgarian", nativeName: "Български", flag: "🇧🇬" },
  { code: "bn", name: "Bengali", nativeName: "বাংলা", flag: "🇧🇩" },
  { code: "bs", name: "Bosnian", nativeName: "Bosanski", flag: "🇧🇦" },
  { code: "ca", name: "Catalan", nativeName: "Català", flag: "🇪🇸" },
  { code: "cs", name: "Czech", nativeName: "Čeština", flag: "🇨🇿" },
  { code: "cy", name: "Welsh", nativeName: "Cymraeg", flag: "🏴" },
  { code: "da", name: "Danish", nativeName: "Dansk", flag: "🇩🇰" },
  { code: "de", name: "German", nativeName: "Deutsch", flag: "🇩🇪" },
  { code: "el", name: "Greek", nativeName: "Ελληνικά", flag: "🇬🇷" },
  { code: "es", name: "Spanish", nativeName: "Español", flag: "🇪🇸" },
  { code: "et", name: "Estonian", nativeName: "Eesti", flag: "🇪🇪" },
  { code: "fa", name: "Persian", nativeName: "فارسی", flag: "🇮🇷" },
  { code: "fi", name: "Finnish", nativeName: "Suomi", flag: "🇫🇮" },
  { code: "fil", name: "Filipino", nativeName: "Filipino", flag: "🇵🇭" },
  { code: "fr", name: "French", nativeName: "Français", flag: "🇫🇷" },
  { code: "gl", name: "Galician", nativeName: "Galego", flag: "🇪🇸" },
  { code: "gu", name: "Gujarati", nativeName: "ગુજરાતી", flag: "🇮🇳" },
  { code: "ha", name: "Hausa", nativeName: "Hausa", flag: "🇳🇬" },
  { code: "he", name: "Hebrew", nativeName: "עברית", flag: "🇮🇱" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी", flag: "🇮🇳" },
  { code: "hr", name: "Croatian", nativeName: "Hrvatski", flag: "🇭🇷" },
  { code: "hu", name: "Hungarian", nativeName: "Magyar", flag: "🇭🇺" },
  { code: "hy", name: "Armenian", nativeName: "Հայերեն", flag: "🇦🇲" },
  {
    code: "id",
    name: "Indonesian",
    nativeName: "Bahasa Indonesia",
    flag: "🇮🇩",
  },
  { code: "ig", name: "Igbo", nativeName: "Igbo", flag: "🇳🇬" },
  { code: "is", name: "Icelandic", nativeName: "Íslenska", flag: "🇮🇸" },
  { code: "it", name: "Italian", nativeName: "Italiano", flag: "🇮🇹" },
  { code: "ja", name: "Japanese", nativeName: "日本語", flag: "🇯🇵" },
  { code: "jv", name: "Javanese", nativeName: "Basa Jawa", flag: "🇮🇩" },
  { code: "ka", name: "Georgian", nativeName: "ქართული", flag: "🇬🇪" },
  { code: "kk", name: "Kazakh", nativeName: "Қазақ тілі", flag: "🇰🇿" },
  { code: "km", name: "Khmer", nativeName: "ភាសាខ្មែរ", flag: "🇰🇭" },
  { code: "kn", name: "Kannada", nativeName: "ಕನ್ನಡ", flag: "🇮🇳" },
  { code: "ko", name: "Korean", nativeName: "한국어", flag: "🇰🇷" },
  { code: "ku", name: "Kurdish", nativeName: "Kurdî", flag: "🇮🇶" },
  { code: "ky", name: "Kyrgyz", nativeName: "Кыргызча", flag: "🇰🇬" },
  { code: "lo", name: "Lao", nativeName: "ລາວ", flag: "🇱🇦" },
  { code: "lt", name: "Lithuanian", nativeName: "Lietuvių", flag: "🇱🇹" },
  { code: "lv", name: "Latvian", nativeName: "Latviešu", flag: "🇱🇻" },
  { code: "mi", name: "Maori", nativeName: "Te Reo Māori", flag: "🇳🇿" },
  { code: "mk", name: "Macedonian", nativeName: "Македонски", flag: "🇲🇰" },
  { code: "ml", name: "Malayalam", nativeName: "മലയാളം", flag: "🇮🇳" },
  { code: "mn", name: "Mongolian", nativeName: "Монгол", flag: "🇲🇳" },
  { code: "mr", name: "Marathi", nativeName: "मराठी", flag: "🇮🇳" },
  { code: "ms", name: "Malay", nativeName: "Bahasa Melayu", flag: "🇲🇾" },
  { code: "my", name: "Burmese", nativeName: "မြန်မာဘာသာ", flag: "🇲🇲" },
  {
    code: "nb",
    name: "Norwegian Bokmål",
    nativeName: "Norsk Bokmål",
    flag: "🇳🇴",
  },
  { code: "ne", name: "Nepali", nativeName: "नेपाली", flag: "🇳🇵" },
  { code: "nl", name: "Dutch", nativeName: "Nederlands", flag: "🇳🇱" },
  { code: "or", name: "Odia", nativeName: "ଓଡ଼ିଆ", flag: "🇮🇳" },
  { code: "pa", name: "Punjabi", nativeName: "ਪੰਜਾਬੀ", flag: "🇮🇳" },
  { code: "pl", name: "Polish", nativeName: "Polski", flag: "🇵🇱" },
  { code: "ps", name: "Pashto", nativeName: "پښتو", flag: "🇦🇫" },
  { code: "pt", name: "Portuguese", nativeName: "Português", flag: "🇵🇹" },
  { code: "ro", name: "Romanian", nativeName: "Română", flag: "🇷🇴" },
  { code: "ru", name: "Russian", nativeName: "Русский", flag: "🇷🇺" },
  { code: "sd", name: "Sindhi", nativeName: "سنڌي", flag: "🇵🇰" },
  { code: "si", name: "Sinhala", nativeName: "සිංහල", flag: "🇱🇰" },
  { code: "sk", name: "Slovak", nativeName: "Slovenčina", flag: "🇸🇰" },
  { code: "sl", name: "Slovenian", nativeName: "Slovenščina", flag: "🇸🇮" },
  { code: "so", name: "Somali", nativeName: "Soomaali", flag: "🇸🇴" },
  { code: "sq", name: "Albanian", nativeName: "Shqip", flag: "🇦🇱" },
  { code: "sr", name: "Serbian", nativeName: "Српски", flag: "🇷🇸" },
  { code: "su", name: "Sundanese", nativeName: "Basa Sunda", flag: "🇮🇩" },
  { code: "sv", name: "Swedish", nativeName: "Svenska", flag: "🇸🇪" },
  { code: "sw", name: "Swahili", nativeName: "Kiswahili", flag: "🇹🇿" },
  { code: "ta", name: "Tamil", nativeName: "தமிழ்", flag: "🇮🇳" },
  { code: "te", name: "Telugu", nativeName: "తెలుగు", flag: "🇮🇳" },
  { code: "tg", name: "Tajik", nativeName: "Тоҷикӣ", flag: "🇹🇯" },
  { code: "th", name: "Thai", nativeName: "ไทย", flag: "🇹🇭" },
  { code: "tl", name: "Tagalog", nativeName: "Tagalog", flag: "🇵🇭" },
  { code: "tr", name: "Turkish", nativeName: "Türkçe", flag: "🇹🇷" },
  { code: "uk", name: "Ukrainian", nativeName: "Українська", flag: "🇺🇦" },
  { code: "ur", name: "Urdu", nativeName: "اردو", flag: "🇵🇰" },
  { code: "uz", name: "Uzbek", nativeName: "Oʻzbekcha", flag: "🇺🇿" },
  { code: "vi", name: "Vietnamese", nativeName: "Tiếng Việt", flag: "🇻🇳" },
  { code: "yo", name: "Yoruba", nativeName: "Yorùbá", flag: "🇳🇬" },
  { code: "zh", name: "Chinese", nativeName: "中文", flag: "🇨🇳" },
  { code: "zu", name: "Zulu", nativeName: "isiZulu", flag: "🇿🇦" },
] as const;

export type SupportedLanguage = (typeof LANGUAGE_METADATA)[number]["code"];
export type TargetLanguage = Exclude<SupportedLanguage, "en">;

export const SUPPORTED_LANGUAGES = LANGUAGE_METADATA.map(
  (lang) => lang.code,
) as readonly SupportedLanguage[];
export const TARGET_LANGUAGES = SUPPORTED_LANGUAGES.filter(
  (code) => code !== "en",
) as readonly TargetLanguage[];

export const LANGUAGE_NAME_MAP = LANGUAGE_METADATA.reduce(
  (acc, lang) => {
    acc[lang.code as SupportedLanguage] = lang.name;
    return acc;
  },
  {} as Record<SupportedLanguage, string>,
);

export const LANGUAGE_NATIVE_NAME_MAP = LANGUAGE_METADATA.reduce(
  (acc, lang) => {
    acc[lang.code as SupportedLanguage] = lang.nativeName;
    return acc;
  },
  {} as Record<SupportedLanguage, string>,
);

export const LANGUAGE_FLAG_MAP = LANGUAGE_METADATA.reduce(
  (acc, lang) => {
    acc[lang.code as SupportedLanguage] = lang.flag;
    return acc;
  },
  {} as Record<SupportedLanguage, string>,
);

export const LANGUAGE_COUNT = SUPPORTED_LANGUAGES.length;
export const TARGET_LANGUAGE_COUNT = TARGET_LANGUAGES.length;

// RTL languages
export const RTL_LANGUAGES = new Set(["ar", "he", "fa", "ps", "sd", "ur"]);

export type LanguageDirection = "ltr" | "rtl";

export function isRtlLanguage(language: string | null | undefined): boolean {
  if (typeof language !== "string") return false;
  const normalized = language.trim().toLowerCase();
  if (!normalized) return false;
  const base = normalized.split("-")[0] || "";
  return RTL_LANGUAGES.has(base);
}

export function getLanguageDirection(
  language: string | null | undefined,
): LanguageDirection {
  return isRtlLanguage(language) ? "rtl" : "ltr";
}

/**
 * Map common language codes (browser / legacy) to VidSeeds.ai-supported codes.
 */
export const LANGUAGE_CODE_ALIASES: Record<string, SupportedLanguage> = {
  // Norwegian: browsers often use nb/nn, app uses "nb"
  nn: "nb",
  no: "nb",
  // Filipino: browsers often use fil, app uses "fil"
  eng: "en",
  rus: "ru",
  in: "id", // Indonesian
  iw: "he", // Hebrew (legacy)
  zh_CN: "zh", // Simplified Chinese
  zh_TW: "zh", // Traditional Chinese
};

export function isSupportedLanguage(code: string): code is SupportedLanguage {
  return Boolean(LANGUAGE_NAME_MAP[code as SupportedLanguage]);
}

/**
 * Normalize any incoming locale/language tag to a supported VidSeeds.ai language code.
 */
export function normalizeToSupportedLanguage(
  input: string | null | undefined,
): SupportedLanguage | null {
  if (!input) return null;

  const trimmed = input.trim();
  if (!trimmed) return null;

  const firstToken = trimmed.split(",")[0]?.trim();
  if (!firstToken || firstToken === "*") return null;

  const tag = firstToken.split(";")[0]?.trim().replace(/_/g, "-").toLowerCase();
  if (!tag) return null;

  const base = tag.split("-")[0] || "";
  const mapped = LANGUAGE_CODE_ALIASES[base] || (base as SupportedLanguage);
  return isSupportedLanguage(mapped) ? mapped : null;
}

export function coerceToSupportedLanguage(
  input: string | null | undefined,
  fallback: SupportedLanguage = "en",
): SupportedLanguage {
  return normalizeToSupportedLanguage(input) || fallback;
}

/**
 * Pick the best supported language from an Accept-Language header.
 */
export function pickSupportedLanguageFromAcceptLanguage(
  acceptLanguage: string | null | undefined,
  fallback: SupportedLanguage = "en",
): SupportedLanguage {
  if (!acceptLanguage) return fallback;

  const candidates = acceptLanguage
    .split(",")
    .map((raw) => {
      const part = raw.trim();
      if (!part) return null;
      const [range, ...params] = part.split(";").map((p) => p.trim());
      if (!range || range === "*") return null;
      const qParam = params.find((p) => p.toLowerCase().startsWith("q="));
      const q = qParam ? Number.parseFloat(qParam.split("=")[1] || "") : 1;
      return { range, q: Number.isFinite(q) ? q : 1 };
    })
    .filter((c): c is { range: string; q: number } => Boolean(c))
    .sort((a, b) => b.q - a.q);

  for (const candidate of candidates) {
    const supported = normalizeToSupportedLanguage(candidate.range);
    if (supported) return supported;
  }

  return fallback;
}

export function getLanguageName(code: string): string {
  if (isSupportedLanguage(code)) {
    return LANGUAGE_NAME_MAP[code];
  }
  return code;
}

export function getLanguageNativeName(code: string): string {
  if (isSupportedLanguage(code)) {
    return LANGUAGE_NATIVE_NAME_MAP[code];
  }
  return getLanguageName(code);
}

export function getLanguageFlag(code: string): string {
  if (isSupportedLanguage(code)) {
    return LANGUAGE_FLAG_MAP[code];
  }
  return "🌐";
}

export function getLanguageMetadata(
  code: string,
): LanguageMetadata | undefined {
  return LANGUAGE_METADATA.find((lang) => lang.code === code);
}

/**
 * 32 dubbing languages supported by ElevenLabs
 */
export const DUBBING_LANGUAGES = [
  "en",
  "hi",
  "pt",
  "zh",
  "es",
  "fr",
  "de",
  "ja",
  "ar",
  "ru",
  "ko",
  "id",
  "it",
  "nl",
  "tr",
  "pl",
  "sv",
  "fil",
  "ms",
  "ro",
  "uk",
  "el",
  "cs",
  "da",
  "fi",
  "bg",
  "hr",
  "sk",
  "ta",
  "hu",
  "nb",
  "vi",
] as const;

export type DubbingLanguage = (typeof DUBBING_LANGUAGES)[number];
export const DUBBING_LANGUAGE_COUNT = DUBBING_LANGUAGES.length;

export function isDubbingLanguage(code: string): code is DubbingLanguage {
  return (DUBBING_LANGUAGES as readonly string[]).includes(code);
}
